import axios from "axios";
import CryptoJs from "crypto-js";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

// Extend dayjs with timezone and UTC plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Helper function to determine if we should use previous day data (weekends/holidays)
function shouldUsePreviousDayData(): boolean {
	const now = dayjs.tz(new Date(), "America/New_York");
	const currentDay = now.day(); // 0 = Sunday, 6 = Saturday
	const currentHour = now.hour();

	// Weekend check
	if (currentDay === 0 || currentDay === 6) {
		return true;
	}

	// Before market hours (before 9:30 AM ET)
	if (currentHour < 9 || (currentHour === 9 && now.minute() < 30)) {
		return true;
	}

	// After market hours (after 4:00 PM ET)
	if (currentHour >= 16) {
		return true;
	}

	return false;
}

// Helper function to get the appropriate time filter based on market status
function getTimeFilter(): string {
	if (shouldUsePreviousDayData()) {
		// Use data from the last trading day before market close
		return "AND LAST_TRADE_TIME > '09:30' AND LAST_TRADE_TIME < '16:00'";
	} else {
		// Use current day data during market hours
		return "AND LAST_TRADE_TIME > '09:30' AND LAST_TRADE_TIME < '16:00'";
	}
}

// Helper function to get the reference date for data queries
function getReferenceDate(): string {
	const now = dayjs.tz(new Date(), "America/New_York");
	const currentDay = now.day(); // 0 = Sunday, 6 = Saturday
	const currentHour = now.hour();

	// If it's past 4pm EST and before Monday 6am, use Friday 4pm as reference
	if (
		currentHour >= 16 ||
		currentDay === 0 ||
		(currentDay === 1 && currentHour < 6)
	) {
		// Find the most recent Friday 4pm
		let referenceDate = now;

		if (currentDay === 0) {
			// Sunday - go back to Friday
			referenceDate = now.subtract(2, "day");
		} else if (currentDay === 1 && currentHour < 6) {
			// Monday before 6am - go back to Friday
			referenceDate = now.subtract(3, "day");
		} else {
			// After 4pm on weekdays - go back to same day 4pm
			referenceDate = now.subtract(1, "day");
		}

		// Set to 4pm (16:00) on the reference date
		referenceDate = referenceDate.hour(16).minute(0).second(0).millisecond(0);

		const result = referenceDate.format("ddd, D MMM YYYY");
		return result;
	}

	// During market hours, use current date
	const result = now.format("ddd, D MMM YYYY");
	return result;
}

// Main function to fetch stock filter data
export async function fetchStockFilterData(
	filter: "most_active" | "top_gainers" | "top_losers",
	serverUrls: any,
	limit: number = 10
): Promise<any[]> {
	try {
		const date = getReferenceDate();

		const secretToken = CryptoJs.MD5(date).toString();

		const headers = {
			secretToken,
			Accept: "application/json"
		};

		const timeFilter = getTimeFilter();
		const usePrevDay = shouldUsePreviousDayData();

		let query_filter: string;

		switch (filter) {
			case "most_active":
				query_filter = `VOLUME > 5000000 AND MARKET_CAP > 2000000000 AND MARKET IN ('NGS','NYE','AMX') ORDER BY VOLUME DESC LIMIT ${limit}`;
				break;
			case "top_gainers":
				query_filter = `PC_CHG > 0 AND VOLUME > 2000000 AND MARKET_CAP > 1000000000 AND MARKET IN ('NGS','NYE','AMX') ${timeFilter} ORDER BY PC_CHG DESC LIMIT ${limit}`;
				break;
			case "top_losers":
				query_filter = `PC_CHG < 0 AND VOLUME > 2000000 AND MARKET_CAP > 1000000000 AND MARKET IN ('NGS','NYE','AMX') ${timeFilter} ORDER BY PC_CHG DESC LIMIT ${limit}`;
				break;
			default:
				throw new Error(`Invalid filter: ${filter}`);
		}

		const data = {
			filter: query_filter,
			fields:
				"BID,PE_RATIO, VOLUME, TRADES, FLOAT, SHORT_INTEREST,SHORT_INTEREST_RATIO, SYMBOL, NAME, LAST,LAST_TRADE_TIME, NET_CHG, PC_CHG, MARKET_CAP, HIGH_52W, LOW_52W, LOW, HIGH, DAY_RANGE, MARKET, SECTOR",
			prevDay: usePrevDay
		};

		const res = await axios.post(
			serverUrls["/httpQueryBasedFilterServer"],
			data,
			{ headers }
		);

		if (res.status !== 200) {
			throw new Error(`API returned status ${res.status}`);
		}

		return res.data || [];
	} catch (error: any) {
		console.error(
			`[fetchStockFilterData] Error fetching ${filter} data:`,
			error.message
		);
		// Fallback to predefined symbols on error
		return [];
	}
}

// Convenience functions for specific filters
export const fetchMostActiveStocks = (serverUrls: any, limit: number = 10) =>
	fetchStockFilterData("most_active", serverUrls, limit);

export const fetchTopGainers = (serverUrls: any, limit: number = 10) =>
	fetchStockFilterData("top_gainers", serverUrls, limit);

export const fetchTopLosers = (serverUrls: any, limit: number = 10) =>
	fetchStockFilterData("top_losers", serverUrls, limit);

// Function to fetch all three filters at once
export async function fetchAllStockFilters(
	serverUrls: any,
	limit: number = 10
): Promise<{
	most_active: any[];
	top_gainers: any[];
	top_losers: any[];
}> {
	try {
		const [most_active, top_gainers, top_losers] = await Promise.all([
			fetchStockFilterData("most_active", serverUrls, limit),
			fetchStockFilterData("top_gainers", serverUrls, limit),
			fetchStockFilterData("top_losers", serverUrls, limit)
		]);

		return {
			most_active,
			top_gainers,
			top_losers
		};
	} catch (error) {
		console.error(`[fetchAllStockFilters] Error fetching all filters:`, error);
		return {
			most_active: [],
			top_gainers: [],
			top_losers: []
		};
	}
}
